import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    CheckCircleRounded,
    DescriptionRounded,
    Group,
    PlayArrowRounded,
} from '@mui/icons-material'
import { Box, LinearProgress, useTheme } from '@mui/material'
import { TEST, VIDEO } from '../../../../utils/constants'
import { SectionState } from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    section: SectionState
    language: string
    isLast: boolean
    selectedSection: SectionState
    handleSelect: () => void
}

const Section: React.FC<Props> = ({
    section = null,
    selectedSection = null,
    language = 'KZ',
    isLast,
    handleSelect,
}) => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { courseSubjectId } = useParams()
    const { state } = useLocation()

    const styles = {
        wrapper: {
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderBottom: isLast ? '' : `1px solid ${theme.palette.grey[200]}`,
            cursor: 'pointer',
            background: section?.id === selectedSection?.id ? ui.surface : null,
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        typeIcon: {
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
        },
        title: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        studentsIcon: {
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 1,
        },
    }

    const TypeIcon = () =>
        section.type === VIDEO ? <PlayArrowRounded /> : <DescriptionRounded />

    const showCircleRounded = () => {
        const { type, percentage } = section
        const isFullWatched = type === VIDEO && percentage === 100
        const isPassedTest = type === TEST && percentage !== null

        if (isFullWatched || isPassedTest) {
            return (
                <CheckCircleRounded
                    color="secondary"
                    sx={{ mr: 1 }}
                />
            )
        }
    }

    return (
        <Box
            sx={styles.wrapper}
            onClick={() => handleSelect()}
        >
            <Box sx={styles.typeIcon}>
                <TypeIcon />
            </Box>

            <Box sx={{ width: '100%' }}>
                <Box sx={styles.title}>
                    <Box>{section.title}</Box>

                    <Box sx={styles.flexCenter}>
                        {showCircleRounded()}
                        {section.percentage ?? '0'}%
                    </Box>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={section.percentage}
                    color={'primary'}
                    sx={{ mt: 1 }}
                />
            </Box>

            <Box
                sx={styles.studentsIcon}
                onClick={() =>
                    navigate(
                        `/teacher-courses/section-analysis/${courseSubjectId}/${section.id}/${section.type}/${language}`,
                        {
                            state: {
                                course: state?.course,
                                subject: state?.subject,
                                section,
                            },
                        },
                    )
                }
            >
                <Group />
            </Box>
        </Box>
    )
}

export default Section
